import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { CheckCircle2, Loader2 } from "lucide-react";
import PlotCompass, { emptySides, emptySideLengths } from "./PlotCompass";

const DIRECTIONS = ["N","S","E","W","NE","NW","SE","SW"];

const PLOT_SURROUNDINGS_KEY = "nc:plotSurroundings";

export default function LeadCaptureForm({ source = "home_form", planId = null, plotDetails = null, prefill = null, compact = false, dataTestidPrefix = "lead", submitLabel = null, messageLabel = null, allowNonRectangular = false }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", message: "",
    state: "", district: "",
    plotLength: "", plotWidth: "", facing: "",
    ...emptySides(),
    ...emptySideLengths(),
  });
  const [nonRectangular, setNonRectangular] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (compact) return;
    try {
      const cached = sessionStorage.getItem(PLOT_SURROUNDINGS_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Object.values(parsed).some(Boolean)) {
          setForm((f) => ({ ...f, ...parsed }));
        }
      }
    } catch {}
  }, [compact]);

  useEffect(() => {
    if (!prefill) return;
    const patch = {};
    if (prefill.length) patch.plotLength = String(prefill.length);
    if (prefill.width)  patch.plotWidth  = String(prefill.width);
    if (prefill.facing) patch.facing     = prefill.facing;
    if (prefill.northSide) patch.northSide = prefill.northSide;
    if (prefill.southSide) patch.southSide = prefill.southSide;
    if (prefill.eastSide)  patch.eastSide  = prefill.eastSide;
    if (prefill.westSide)  patch.westSide  = prefill.westSide;
    if (Object.keys(patch).length) setForm((f) => ({ ...f, ...patch }));
  }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      setError(t("leadForm.errRequired"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const hasPlotInfo = form.plotLength || form.plotWidth || form.facing ||
        form.northSide || form.southSide || form.eastSide || form.westSide ||
        form.northLength || form.southLength || form.eastLength || form.westLength;
      const plotInfo = hasPlotInfo ? {
        length: form.plotLength || null,
        width: form.plotWidth || null,
        facing: form.facing || null,
        north_side: form.northSide || null,
        south_side: form.southSide || null,
        east_side: form.eastSide || null,
        west_side: form.westSide || null,
        non_rectangular: nonRectangular || null,
        north_length: nonRectangular ? (form.northLength || null) : null,
        south_length: nonRectangular ? (form.southLength || null) : null,
        east_length: nonRectangular ? (form.eastLength || null) : null,
        west_length: nonRectangular ? (form.westLength || null) : null,
      } : null;
      await api.post("/leads", {
        name: form.name, mobile: form.mobile, email: form.email || null,
        state: form.state || null, district: form.district || null,
        source, plot_details: plotDetails || plotInfo, plan_id: planId,
        message: form.message || null,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-emerald-200 rounded-2xl p-6 flex items-start gap-3" data-testid={`${dataTestidPrefix}-form-success`}>
        <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-slate-900">{t("leadForm.thankYou")}, {form.name}!</div>
          <div className="text-sm text-slate-600 mt-1">{t("leadForm.success")}</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`bg-white rounded-2xl border border-slate-200 nc-soft-shadow ${compact ? "p-5" : "p-6 md:p-8"}`} data-testid={`${dataTestidPrefix}-form`}>
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.name")}</label>
          <input className="nc-input mt-1" data-testid={`${dataTestidPrefix}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.mobile")}</label>
          <input className="nc-input mt-1" data-testid={`${dataTestidPrefix}-mobile-input`} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required pattern="[0-9]{10}" maxLength="10" />
        </div>
        <div className={compact ? "" : "sm:col-span-2"}>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.email")}</label>
          <input type="email" className="nc-input mt-1" data-testid={`${dataTestidPrefix}-email-input`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>

        {!compact && (
          <>
            {/* Plot dimensions */}
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.plotLength")}</label>
              <input type="number" className="nc-input mt-1" data-testid={`${dataTestidPrefix}-plot-length-input`} value={form.plotLength} onChange={(e) => setForm({ ...form, plotLength: e.target.value })} placeholder={t("leadForm.plotLengthPh")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.plotWidth")}</label>
              <input type="number" className="nc-input mt-1" data-testid={`${dataTestidPrefix}-plot-width-input`} value={form.plotWidth} onChange={(e) => setForm({ ...form, plotWidth: e.target.value })} placeholder={t("leadForm.plotWidthPh")} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.facingLabel")}</label>
              <select className="nc-input mt-1" data-testid={`${dataTestidPrefix}-facing-select`} value={form.facing} onChange={(e) => setForm({ ...form, facing: e.target.value })}>
                <option value="">{t("leadForm.selectFacing")}</option>
                {DIRECTIONS.map((v) => <option key={v} value={v}>{t(`facing.${v}`)}</option>)}
              </select>
            </div>

            {/* Plot surroundings */}
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">{t("leadForm.surroundings")}</p>
              {allowNonRectangular && (
                <label className="mb-3 inline-flex items-center gap-2 text-sm text-slate-700" data-testid={`${dataTestidPrefix}-nonrect-toggle`}>
                  <input
                    type="checkbox"
                    checked={nonRectangular}
                    onChange={(e) => setNonRectangular(e.target.checked)}
                    className="accent-emerald-600 w-4 h-4"
                  />
                  My plot is not rectangular
                </label>
              )}
              <PlotCompass
                value={{
                  northSide: form.northSide,
                  southSide: form.southSide,
                  eastSide:  form.eastSide,
                  westSide:  form.westSide,
                  northLength: form.northLength,
                  southLength: form.southLength,
                  eastLength:  form.eastLength,
                  westLength:  form.westLength,
                }}
                onChange={(next) => setForm({ ...form, ...next })}
                testidPrefix={`${dataTestidPrefix}-compass`}
                showDimensions={allowNonRectangular && nonRectangular}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.state")}</label>
              <input className="nc-input mt-1" data-testid={`${dataTestidPrefix}-state-input`} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder={t("leadForm.statePh")} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("leadForm.district")}</label>
              <input className="nc-input mt-1" data-testid={`${dataTestidPrefix}-district-input`} value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} placeholder={t("leadForm.districtPh")} />
            </div>
          </>
        )}

        <div className={compact ? "" : "sm:col-span-2"}>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{messageLabel || t("leadForm.message")}</label>
          <textarea rows={3} className="nc-input mt-1" data-testid={`${dataTestidPrefix}-message-input`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600" data-testid={`${dataTestidPrefix}-form-error`}>{error}</p>}
      <button type="submit" disabled={loading} className="mt-5 nc-btn-primary w-full sm:w-auto" data-testid={`${dataTestidPrefix}-submit-btn`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {submitLabel || t("leadForm.submit")}
      </button>
    </form>
  );
}
